import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import moment from "moment";
import SvgIcon from "../../../../parts/SvgIcon";
import submit from "submit-form";
import { getOrganizationCoach } from "../../../../utils/user-helpers";
import { getTeamConversation } from "../../../../utils/chat-util";

const PULL_CHAT_MIGRATION_CUTOFF = new Date("2026-05-19");

const TeamMembers = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const cycle = locals?.editCycle;
  const referAsClients = locals?.activeUser?.referAsClients;
  const referAsClientsDisplay = referAsClients
    ? t("athletes.roleArticleClient")
    : t("athletes.roleArticleAthlete");
  const { usersOrganization = { organizationActiveUsers: [] }, activeUser } =
    locals;
  const { organizationActiveUsers = [] } = usersOrganization;
  const pullCommunityEnabled =
    locals?.activeUserToRequest?.pullAccountInfo?.inAppCommunityEnabled;

  const [members, setMembers] = useState(
    cycle && cycle?.members ? cycle.members : []
  );
  const [isNameAsc, setIsNameAsc] = useState(false);
  const [isDateAsc, setIsDateAsc] = useState(false);
  const [isUserTypeAsc, setIsUserTypeAsc] = useState(false);
  const [chatGuestIds, setChatGuestIds] = useState(null);

  const shouldShowChatColumn = useMemo(() => {
    if (!pullCommunityEnabled) return false;
    if (cycle?.status !== "published") return false;
    if (!cycle?.createdDate) return false;
    return new Date(cycle.createdDate) < PULL_CHAT_MIGRATION_CUTOFF;
  }, [pullCommunityEnabled, cycle?.status, cycle?.createdDate]);

  useEffect(() => {
    setMembers(cycle?.members ? [...cycle.members] : []);
  }, [cycle?.members]);

  useEffect(() => {
    if (!shouldShowChatColumn || !api || !cycle?._id) return;
    const ownerId = locals?.activeUserToRequest?._id;
    if (!ownerId) return;

    let cancelled = false;
    (async () => {
      try {
        
        const result = await getTeamConversation(api, cycle._id, ownerId);
        if (cancelled) return;
        if (!result) {
          setChatGuestIds(new Set());
          return;
        }
        const rawGuests = result.custom?.guests;
        let guestIds = [];
        if (Array.isArray(rawGuests)) {
          guestIds = rawGuests;
        } else if (typeof rawGuests === "string" && rawGuests.length > 0) {
          try {
            const parsed = JSON.parse(rawGuests);
            if (Array.isArray(parsed)) guestIds = parsed;
          } catch {
            guestIds = rawGuests
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean);
          }
        }
        setChatGuestIds(new Set(guestIds.map(String)));
      } catch (err) {
        console.error("Failed to load team chat guests:", err);
        if (!cancelled) setChatGuestIds(new Set());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldShowChatColumn, api, cycle?._id, locals?.activeUserToRequest?._id]);

  const sortByName = () => {
    const newMembers = [...members];
    const sorted = newMembers.sort((a, b) =>
      isNameAsc
        ? a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        : b.name.localeCompare(a.name, undefined, { sensitivity: "base" })
    );

    setMembers(sorted);
    setIsNameAsc(!isNameAsc); // toggle direction
  };

  const sortByDate = () => {
    const newMembers = [...members];
    const sorted = newMembers.sort((a, b) => {
      const dateA = new Date(a.joinDate);
      const dateB = new Date(b.joinDate);

      return isDateAsc ? dateA - dateB : dateB - dateA;
    });

    setMembers(sorted);
    setIsDateAsc(!isDateAsc);
  };

  const sortByUserType = () => {
    const newMembers = [...members];
    const sorted = newMembers.sort((a, b) => {
      const typeA = a.userType || "PUSH";
      const typeB = b.userType || "PUSH";
      return isUserTypeAsc
        ? typeB.localeCompare(typeA)
        : typeA.localeCompare(typeB);
    });
    setMembers(sorted);
    setIsUserTypeAsc(!isUserTypeAsc);
  };

  return (
    <div className="members-table" style={{ marginTop: 24 }}>
      {members && members.length > 0 && (
        <section role="table-wrapper">
          <table role="report-table">
            <thead role="report-header">
              <tr>
                <th>
                  {t("teams.teamMembers.members")}{" "}
                  <SvgIcon
                    onClickFn={sortByName}
                    src="/img/svg/up_down_arrows.svg"
                  />
                </th>
                <th>
                  {t("teams.teamMembers.joiningDate")}{" "}
                  <SvgIcon
                    onClickFn={sortByDate}
                    src="/img/svg/up_down_arrows.svg"
                  />
                </th>
                <th>
                  {t("teams.teamMembers.userType")}{" "}
                  <SvgIcon
                    onClickFn={sortByUserType}
                    src="/img/svg/up_down_arrows.svg"
                  />
                </th>
                <th>{t("teams.teamMembers.email")}</th>
                {shouldShowChatColumn && (
                  <th>{t("teams.teamMembers.chat")}</th>
                )}
                <th>{t("teams.teamMembers.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const { isFromOtherOrganizationMember, organizationCoachId } =
                  getOrganizationCoach(
                    activeUser,
                    member.id,
                    organizationActiveUsers
                  );
                const hasHref = member.userType === "PULL";
                const profileHref = hasHref
                  ? `/athlete-portal/profile/?athlete=${member.id}&section=dashboard${isFromOtherOrganizationMember ? `&organizationCoach=${organizationCoachId}` : ""}`
                  : undefined;

                const avatarImg = (
                  <img
                    src={getAvatarUrl(member, null)}
                    loading="lazy"
                    className="profile-picture"
                    alt=""
                  />
                );

                return (
                  <tr key={`${member.id}-team-member`}>
                    <td>
                      <div className="username-cell">
                        {hasHref ? (
                          <a className="image" href={profileHref}>
                            {avatarImg}
                          </a>
                        ) : (
                          <span className="image">{avatarImg}</span>
                        )}
                        {hasHref ? (
                          <a className="name" href={profileHref}>
                            {member.name}
                          </a>
                        ) : (
                          <p>{member.name}</p>
                        )}
                      </div>
                    </td>
                    <td>
                      {member.joinDate
                        ? moment(member.joinDate).format("D MMM, YYYY")
                        : "-"}
                    </td>
                    <td>{member.userType || "PUSH"}</td>
                    <td>
                      <a href={`mailto:${member.email}`}>{member.email}</a>
                    </td>
                    {shouldShowChatColumn && (
                      <td align="center" valign="middle">
                        {member.userType === "PULL" ? (
                          chatGuestIds === null ? (
                            <span className="p2">—</span>
                          ) : chatGuestIds.has(String(member.id)) ? (
                            <span className="inline-flex gap-2 items-center">
                              <SvgIcon src="/img/new-icons/check-circle.svg" />
                              <span>{t("teams.teamMembers.inChat")}</span>
                            </span>
                          ) : (
                            <button
                              type="button"
                              data-button-type="outline"
                              className="gap-2 items-center"
                              onClick={() => {
                                submit(
                                  `/actions/cycle/add-pull-members-to-team-chat/${cycle._id}`,
                                  {
                                    method: "POST",
                                    body: { memberId: String(member.id) },
                                  }
                                );
                              }}
                            >
                              <SvgIcon
                                src="/img/new-icons/person-add.svg"
                                noFilterOnLight={true}
                              />
                              <span>{t("teams.teamMembers.addToChat")}</span>
                            </button>
                          )
                        ) : null}
                      </td>
                    )}
                    <td className="actions" align="center" valign="middle">
                      <a
                        role="button"
                        data-button-type="secondary"
                        data-action="cancel"
                        data-icon
                        href={`/actions/refresh?modal[type]=remove-member&cycleId=${cycle._id}&memberId=${member.id}&memberName=${member.name}`}
                      >
                        {t("teams.teamMembers.remove")}
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {(!cycle || !cycle?.members || cycle.members.length < 1) && (
        <div className="no-data-state">
          <p className="s1">{t("teams.teamMembers.noMembers")}</p>
          <p className="p1">
            {t("teams.teamMembers.noMembersHint", {
              role: referAsClientsDisplay,
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default TeamMembers;
